import * as React from 'react';
import { MapView } from '@ooh/ui/components/map-view';
import type { MapViewHandle, DrawingTool, MapPin } from '@ooh/ui/components/map-view';
import type { PoiFeature } from '@ooh/ui/components/map-view';
import type { AreaSelectionActions } from '../use-area-selection';
import { isClickSelectTool } from './drawing-tool';

interface MapViewWithDrawingProps {
  mapViewRef: React.RefObject<MapViewHandle | null>;
  activeTool: DrawingTool;
  actions: AreaSelectionActions;
  selectedIds: ReadonlySet<string | number>;
  handleDrawComplete: (geometry: GeoJSON.Polygon, toolUsed: DrawingTool) => void;
  handleDrawProgress: (geometry: GeoJSON.Polygon | null, toolUsed: DrawingTool) => void;
  mapPins: MapPin[];
  poiFeatures: PoiFeature[];
  visiblePoiCategories: Set<string>;
  categoryColors: Record<string, string>;
  lat?: number;
  lng?: number;
  zoom?: number;
  /**
   * Initial camera bounds as [west, south, east, north], restored from the
   * `viewBbox` URL param. Takes priority over lat/lng/zoom for the first
   * render only (see MapView's `initialBounds` prop).
   */
  initialBounds?: [number, number, number, number];
  /** Max zoom for clustering. Passed through to MapView; defaults to MapView's own default (14). */
  clusterMaxZoom?: number;
  /** Cluster radius in px. Passed through to MapView; defaults to MapView's own default (60). */
  clusterRadius?: number;
  /** Position of a standalone marker (e.g. coordinate search result). Null hides it. */
  markerPosition?: { lat: number; lng: number } | null;
  renderPopup: (pin: MapPin, onClose: () => void) => React.ReactNode;
  onPoiClick: (poiId: string) => void;
  selectedPoiId: string | null | undefined;
  renderPoiPopup: ((poiId: string, onClose: () => void) => React.ReactNode) | undefined;
  onBoundsChange: (bbox: string) => void;
  onMapReady: (map: import('@ooh/ui/components/map-view').MapInstance) => void;
}

export function MapViewWithDrawing({
  mapViewRef,
  activeTool,
  actions,
  selectedIds,
  handleDrawComplete,
  handleDrawProgress,
  mapPins,
  poiFeatures,
  visiblePoiCategories,
  categoryColors,
  lat,
  lng,
  zoom,
  initialBounds,
  clusterMaxZoom,
  clusterRadius,
  markerPosition,
  renderPopup,
  onPoiClick,
  selectedPoiId,
  renderPoiPopup,
  onBoundsChange,
  onMapReady,
}: MapViewWithDrawingProps) {
  const dimmedPins = React.useMemo(
    () => (selectedIds.size > 0 ? mapPins.filter((p) => !selectedIds.has(p.id)) : []),
    [mapPins, selectedIds],
  );

  const selectedMapPins = React.useMemo(
    () => (selectedIds.size > 0 ? mapPins.filter((p) => selectedIds.has(p.id)) : mapPins),
    [mapPins, selectedIds],
  );

  return (
    <MapView
      ref={mapViewRef}
      enableDrawing={true}
      currentTool={activeTool}
      onDrawComplete={handleDrawComplete}
      onDrawProgress={handleDrawProgress}
      selectedPinIds={selectedIds}
      dimmedPins={dimmedPins}
      onPinClick={(pinId) => {
        if (activeTool === 'point-add') actions.addMany([pinId]);
        else if (activeTool === 'point-remove') actions.removeMany([pinId]);
        // 'pan' no longer selects pins on click — pure camera navigation
      }}
      pins={selectedMapPins}
      poiData={poiFeatures}
      showPoiLayer={visiblePoiCategories.size > 0}
      visiblePoiCategories={visiblePoiCategories}
      categoryColors={categoryColors}
      className="absolute inset-0"
      initialCenter={lat != null && lng != null ? [lat, lng] : undefined}
      initialZoom={zoom}
      initialBounds={initialBounds}
      clusterMaxZoom={clusterMaxZoom}
      clusterRadius={clusterRadius}
      markerPosition={markerPosition}
      // Suppress detail popups while click-select is active — pin clicks select, not open.
      renderPopup={isClickSelectTool(activeTool) ? () => null : renderPopup}
      onPoiClick={onPoiClick}
      selectedPoiId={selectedPoiId}
      renderPoiPopup={renderPoiPopup}
      onBoundsChange={onBoundsChange}
      onMapReady={onMapReady}
    />
  );
}
